import { apiClient } from "@/lib/api";
import type { Paginated, PaginationParams, RegionCode } from "@/types";

export interface AdminExternalPaymentRow {
  id: string;
  region_id: string;
  region_code: RegionCode;
  booking_id: string | null;
  type: string;
  amount_cents: number;
  currency: string;
  status: string;
  provider: string;
  flow: string;
  provider_payment_id: string | null;
  idempotency_key: string;
  created_at: string;
  updated_at: string;
}

export type PaginatedExternalPayments = Paginated<AdminExternalPaymentRow>;

export interface AdminExternalPaymentListParams extends PaginationParams {
  region_id?: string;
  status?: string;
}

export interface ExternalPaymentStatusCount {
  status: string;
  count: number;
}

/** Per-status counts for the audit status filter (most frequent first). */
export interface ExternalPaymentStatusCounts {
  total: number;
  items: ExternalPaymentStatusCount[];
}

export type AdminEconomicEventListParams = PaginationParams & {
  booking_id?: string;
  external_payment_id?: string;
};

export interface AdminAllocationLineRow {
  id: string;
  economic_event_id: string;
  role_code: string;
  ledger_account_id: string;
  amount_cents: number;
  allocation_step: number;
  stored_cut_percent: number | null;
  absolute_cut_percent: number | null;
}

export interface AdminEconomicEventSummaryRow {
  id: string;
  type: string;
  status: string;
  external_payment_id: string | null;
  booking_id: string | null;
  checkout_id: string | null;
  currency: string;
  gross_amount_cents: number;
  allocation_method: string;
  created_at: string;
  allocation_line_count: number;
}

export type PaginatedEconomicEventSummaries = Paginated<AdminEconomicEventSummaryRow>;

export interface AdminEconomicEventDetail {
  id: string;
  type: string;
  status: string;
  external_payment_id: string | null;
  booking_id: string | null;
  checkout_id: string | null;
  reverses_event_id: string | null;
  currency: string;
  gross_amount_cents: number;
  allocation_method: string;
  allocation_rule_snapshot: Record<string, unknown> | null;
  idempotency_key: string | null;
  metadata_json: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  ledger_entry_count: number;
  allocation_lines: AdminAllocationLineRow[];
}

export const adminPaymentAuditKeys = {
  all: ["admin", "payment-audit"] as const,
  /** Prefix shared with EventsListPage. */
  events: () => ["admin", "payment-events"] as const,
  eventList: (params: AdminEconomicEventListParams) =>
    [...adminPaymentAuditKeys.events(), "list", params] as const,
  event: (eventId: string) =>
    [...adminPaymentAuditKeys.events(), "detail", eventId] as const,
  /** Prefix shared with ExternalPaymentsPage. */
  externalPayments: () => ["admin", "external-payments"] as const,
  externalPaymentList: (params: AdminExternalPaymentListParams) =>
    [...adminPaymentAuditKeys.externalPayments(), "list", params] as const,
  externalPaymentStatusCounts: (regionId: string | null) =>
    [...adminPaymentAuditKeys.externalPayments(), "status-counts", regionId] as const,
  externalPayment: (paymentId: string) =>
    ["admin", "external-payment", paymentId] as const,
};

export const adminPaymentAuditService = {
  async getExternalPayment(
    paymentId: string,
  ): Promise<AdminExternalPaymentRow> {
    const { data } = await apiClient.get<AdminExternalPaymentRow>(
      `/admin/external-payments/${paymentId}`,
    );
    return data;
  },

  async listExternalPayments(
    params: AdminExternalPaymentListParams,
  ): Promise<PaginatedExternalPayments> {
    const { data } = await apiClient.get<PaginatedExternalPayments>(
      "/admin/external-payments",
      { params },
    );
    return data;
  },

  async countExternalPaymentsByStatus(
    regionId: string | null,
  ): Promise<ExternalPaymentStatusCounts> {
    const { data } = await apiClient.get<ExternalPaymentStatusCounts>(
      "/admin/external-payments/status-counts",
      { params: regionId ? { region_id: regionId } : undefined },
    );
    return data;
  },

  async listEconomicEvents(
    params: AdminEconomicEventListParams,
  ): Promise<PaginatedEconomicEventSummaries> {
    const { data } = await apiClient.get<PaginatedEconomicEventSummaries>(
      "/admin/economic-events",
      { params },
    );
    return data;
  },

  async getEconomicEvent(eventId: string): Promise<AdminEconomicEventDetail> {
    const { data } = await apiClient.get<AdminEconomicEventDetail>(
      `/admin/economic-events/${eventId}`,
    );
    return data;
  },
};
